import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DictionaryService } from '../services/dictionaryService';
import { usePageMeta, SITE_NAME, SITE_URL } from '../lib/usePageMeta';
import SearchBar from '../components/SearchBar';
import { Pronunciation, SenseGroup } from '../components/Entry';
import { SaveButton } from '../components/WordActions';

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

const truncate = (text, max) => (text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text);

const WordPage = () => {
  const { word: rawWord } = useParams();
  const navigate = useNavigate();
  const word = decodeURIComponent(rawWord || '').trim();
  const [entry, setEntry] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | not-found | unavailable

  // One URL per word: /define/Serendipity → /define/serendipity
  useEffect(() => {
    if (word && word !== word.toLowerCase()) {
      navigate(`/define/${encodeURIComponent(word.toLowerCase())}`, { replace: true });
    }
  }, [word, navigate]);

  const loadEntry = useCallback(async () => {
    if (!word) return;
    setStatus('loading');
    setEntry(null);
    try {
      const data = await DictionaryService.getEntry(word);
      setEntry(data);
      setStatus('ready');
      DictionaryService.addRecentWord(data.word);
    } catch (error) {
      setStatus(error.message === 'not-found' ? 'not-found' : 'unavailable');
    }
  }, [word]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  const display = capitalize(word);
  const firstGroup = entry?.groups[0];
  const firstDefinition = firstGroup?.senses[0]?.definition || '';

  usePageMeta(
    `Define ${display}: Definition of the Word ${display} | ${SITE_NAME}`,
    firstDefinition
      ? truncate(`Definition of ${word}: ${firstDefinition} See pronunciation, examples and similar words.`, 160)
      : `Definition of ${word}: meaning, pronunciation, examples and similar words.`
  );

  const getRandomWord = () => {
    const words = DictionaryService.getPopularWords().filter((w) => w !== word);
    navigate(`/define/${words[Math.floor(Math.random() * words.length)]}`);
  };

  if (status === 'loading') {
    return (
      <div className="wrap pt-12 md:pt-16" aria-busy="true">
        <p className="text-lg text-ink-soft">Definition of the word</p>
        <p className="mt-2 font-serif text-[clamp(3rem,9vw,6.5rem)] font-semibold leading-[1.05] text-ink/30">{word}</p>
        <div className="mt-10 max-w-2xl space-y-4 motion-safe:animate-pulse">
          <div className="h-5 w-3/4 rounded bg-ink/10" />
          <div className="h-5 w-full rounded bg-ink/10" />
          <div className="h-5 w-2/3 rounded bg-ink/10" />
        </div>
        <span className="sr-only" role="status">Loading the definition of {word}</span>
      </div>
    );
  }

  if (status !== 'ready') {
    const notFound = status === 'not-found';
    return (
      <div className="wrap pt-12 md:pt-16">
        <div className="max-w-2xl">
          <h1 className="font-serif text-4xl font-semibold leading-tight md:text-5xl">
            {notFound ? `No definition found for “${word}”` : 'The definition didn’t load'}
          </h1>
          <p className="mt-4 text-lg text-ink-soft">
            {notFound
              ? 'Check the spelling, or try the base form of the word: run instead of running, happy instead of happiest.'
              : 'The dictionary service didn’t respond. Check your connection and try again.'}
          </p>

          {notFound ? (
            <SearchBar variant="field" className="mt-8" placeholder="Try another word" autoFocus />
          ) : (
            <button
              type="button"
              onClick={loadEntry}
              className="mt-8 h-12 rounded-md bg-ink px-6 font-medium text-paper transition-colors hover:bg-ink/85"
            >
              Try again
            </button>
          )}

          <p className="mt-6 text-ink-soft">
            Or{' '}
            <button
              type="button"
              onClick={getRandomWord}
              className="rounded-sm font-medium text-leaf underline decoration-1 underline-offset-4 hover:decoration-2"
              data-testid="random-word-button"
            >
              look up a random word
            </button>
            .
          </p>
        </div>
      </div>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTerm',
    name: entry.word,
    description: firstDefinition,
    url: `${SITE_URL}/define/${encodeURIComponent(entry.word)}`,
    inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: `${SITE_NAME} Dictionary`,
    },
  };

  return (
    <article className="wrap pt-8 md:pt-12">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <nav aria-label="Breadcrumb" className="text-[0.95rem] text-ink-soft">
        <Link to="/dictionary" className="hover:text-ink">Dictionary</Link>
        <span aria-hidden="true" className="px-2">/</span>
        <span aria-current="page">{entry.word}</span>
      </nav>

      <header className="mt-8 max-w-4xl">
        <h1 className="font-serif">
          <span className="block font-sans text-lg font-normal text-ink-soft">Definition of the word</span>
          <span className="headword mt-1 text-[clamp(3rem,9vw,6.5rem)] font-semibold leading-[1.05] tracking-[-0.02em]">
            {entry.word}
          </span>
        </h1>

        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-3">
          <Pronunciation entry={entry} />
          <SaveButton word={entry.word} className="h-9 px-3.5" />
        </div>

        {firstDefinition && (
          <p className="mt-8 max-w-2xl font-serif text-xl leading-normal md:text-2xl">
            <em className="text-ink-soft">{firstGroup.partOfSpeech}.</em> {firstDefinition}
          </p>
        )}
      </header>

      <div className="mt-12 grid gap-12 lg:grid-cols-[minmax(0,1fr)_15rem]">
        <div className="min-w-0">
          {entry.groups.map((group) => (
            <div
              key={group.partOfSpeech}
              id={`pos-${group.partOfSpeech.replace(/\s+/g, '-')}-section`}
              className="scroll-mt-24"
            >
              <SenseGroup group={group} />
            </div>
          ))}

          {entry.origin && (
            <section className="grid gap-x-10 gap-y-4 border-t border-rule py-8 md:grid-cols-[8.5rem_minmax(0,1fr)]">
              <h2 className="font-serif text-2xl italic">origin</h2>
              <p className="max-w-[42rem] font-serif text-lg leading-[1.7]" data-testid="etymology">{entry.origin}</p>
            </section>
          )}

          <p className="border-t border-rule pt-6 text-sm text-ink-soft">
            Source:{' '}
            {entry.sourceUrls[0] ? (
              <a href={entry.sourceUrls[0]} target="_blank" rel="noopener noreferrer" className="text-link">Wiktionary</a>
            ) : (
              'Wiktionary'
            )}
            {entry.license && (
              <>
                , shared under{' '}
                <a href={entry.license.url} target="_blank" rel="noopener noreferrer" className="text-link">
                  {entry.license.name}
                </a>
              </>
            )}
            .
          </p>
        </div>

        <aside className="space-y-10 self-start text-[0.95rem] lg:sticky lg:top-24">
          {entry.groups.length > 1 && (
            <nav aria-label="On this page">
              <h2 className="font-semibold text-ink">On this page</h2>
              <ul className="mt-3 space-y-2">
                {entry.groups.map((group) => (
                  <li key={group.partOfSpeech}>
                    <a
                      href={`#pos-${group.partOfSpeech.replace(/\s+/g, '-')}-section`}
                      className="text-ink-soft hover:text-ink"
                    >
                      <span className="font-serif italic text-ink">{group.partOfSpeech}</span>
                      {', '}
                      {group.senses.length} {group.senses.length === 1 ? 'meaning' : 'meanings'}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          <div>
            <h2 className="font-semibold text-ink">In another language</h2>
            <Link
              to={`/translations?text=${encodeURIComponent(entry.word)}`}
              className="mt-3 inline-block text-link"
            >
              Translate “{entry.word}”
            </Link>
          </div>

          <div>
            <h2 className="mb-3 font-semibold text-ink">Look up another word</h2>
            <SearchBar variant="field" compact placeholder="Another word" />
          </div>
        </aside>
      </div>
    </article>
  );
};

export default WordPage;
